import { observer } from 'mobx-react';
import React from 'react';
import { Badge, Button, Card, CardContent, Grid, GridColumn, Heading, Icon, Notice, Separator, Text } from '@teliaee/design-system';
import UiNotice from '../components/UiNotice';
import Config from '../Config';
import useStores from '../stores/UseStores';
import { FetchStatus } from '../util/ApiUtil';
import { PasskeyAuthenticateErrorType } from '../util/PasskeyUtil';
import WrapWithContainerInFullPage from '../wrapper/WrapWithContainerInFullPage';
import { PanelType } from './PanelTypes';
import { isWebAuthSupported } from '../util/BrowserUtil';
import LoginInfoNotice from '../components/LoginInfoNotice';

interface SelectedMobilePanelProps {
  panel: PanelType;
  setBackToMethodSelectionClicked: (clicked: boolean) => void;
}

const SelectedMobilePanel = observer((props: SelectedMobilePanelProps): React.JSX.Element => {
  const { panel, setBackToMethodSelectionClicked } = props;
  const {
    translateStore: { translate },
    homeStore: { panels },
    uiStore: { selectedTab, setSelectedTab, hasNotice, hideNotices },
    passkeyStore,
  } = useStores();
  const ChildComponent = panel.component;
  const isPasskeyPanel = panel.id === 'passkey';
  const passkeyExists = passkeyStore.getPasskeyExists();

  const handleBackToMethodSelection = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault();
    hideNotices();
    setBackToMethodSelectionClicked(true);
    setSelectedTab(null);
  };

  const passkeyErrorNotice = () => {
    return (
      <div role="alert">
        {passkeyStore.status === FetchStatus.ERROR && isPasskeyPanel && (
          <>
            <Notice type={passkeyStore.authenticateErrorType === PasskeyAuthenticateErrorType.USER_CANCELLED ? 'attention' : 'error'}>
              {translate(`passkey.error.${passkeyStore.authenticateErrorType}`)}
            </Notice>
            <Separator color="invisible" spacing="xsmall" />
          </>
        )}
      </div>
    );
  };

  return (
    <WrapWithContainerInFullPage>
      {Config.isModule && <Separator spacing="medium" color="invisible" />}
      {passkeyErrorNotice()}
      <Card type="light" background="transparent" borderRadius="large">
        <CardContent>
          <Grid align={['middle-xs']} noWrap horizontalGutter="small" verticalGutter="none" className={panel.id === 'admin' ? 'c-employee-text' : undefined}>
            <GridColumn width={['min']} align={['top-xs']}>
              <Icon modifiers={panel.icon} size="sm" />
            </GridColumn>
            <GridColumn width={['max']}>
              <Grid horizontalGutter="none" verticalGutter="xsmall">
                <GridColumn width={['xs-12']}>
                  <Grid noWrap align={['middle-xs']} horizontalGutter="small" verticalGutter="none">
                    <GridColumn width={['min']}>
                      <Heading level={2} className="h5">
                        {translate(`panel.${panel.id}.label`)}
                      </Heading>
                    </GridColumn>
                    {isPasskeyPanel && (
                      <GridColumn width={['max']}>
                        <Badge size="medium" purpose="recommended" text={translate('panel.badge.new')} />
                      </GridColumn>
                    )}
                  </Grid>
                </GridColumn>
                {isPasskeyPanel && (
                  <>
                    <GridColumn width={['xs-12']}>
                      <Text className="c-neutral-text-subtle text-small" element="p">
                        {translate('panel.passkey.subHeading')}
                      </Text>
                      <Separator color="invisible" spacing="xsmall" weight="thin" />
                      {isWebAuthSupported() && <Text element="p">{translate(`panel.passkey.explanation${passkeyExists ? '.exists' : ''}`)}</Text>}
                    </GridColumn>
                    <GridColumn width={['xs-12']}>
                      <Button link text={translate('panel.passkey.cta')} target="_blank" url={translate('panel.passkey.cta.url')} />
                    </GridColumn>
                  </>
                )}
              </Grid>
            </GridColumn>
          </Grid>
          <Separator spacing="small" color="invisible" />
          <LoginInfoNotice showAuthUpgradeNotice={!isPasskeyPanel} />
          {selectedTab === panel.id && hasNotice && (
            <>
              <UiNotice />
              <Separator spacing="xxsmall" color="invisible" />
            </>
          )}
          <ChildComponent />
        </CardContent>
      </Card>
      {panels.length !== 1 && (
        <>
          <Separator spacing="small" color="invisible" />
          <Button text={translate('panel.otherLoginTypes')} link iconLeft="#arrow-left" onClick={handleBackToMethodSelection} />
        </>
      )}
    </WrapWithContainerInFullPage>
  );
});

export default SelectedMobilePanel;
